'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { Search } from 'lucide-react';
import type { PaginatedResponse, KnowledgeHubSlim, KnowledgeHubCategory } from '@/types';
import { Card } from '@/components/ui/Card';
import { resolveImageUrl } from '@/lib/image';
import { slugifyRouteValue } from '@/lib/slug';

const buildQuery = (params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            query.set(key, String(value));
        }
    });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

export const KnowledgeHubListView: React.FC<{
    data: PaginatedResponse<KnowledgeHubSlim>;
    page: number;
    search?: string;
    category?: KnowledgeHubCategory;
    categories: KnowledgeHubCategory[];
}> = ({ data, page, search, category, categories }) => {
    const { t, i18n } = useTranslation();
    return (
        <div className="blog-page">
            <div className="container">
                <div className="page-header">
                    <h1>{t('knowledgeHub.title')}</h1>
                    <p>{t('knowledgeHub.subtitle')}</p>
                </div>

                <div className="search-section">
                    <form className="input-wrapper" action="/knowledge-hub" method="get">
                        <label className="input-label" htmlFor="knowledge-search">
                            {t('common.search')}
                        </label>
                        <div className="input-container">
                            <span className="input-icon">
                                <Search size={20} />
                            </span>
                            <input
                                id="knowledge-search"
                                className="input input-with-icon"
                                type="text"
                                name="search"
                                placeholder={t('common.searchPlaceholder')}
                                defaultValue={search || ''}
                            />
                            {category && <input type="hidden" name="category" value={category} />}
                        </div>
                    </form>
                </div>

                <div
                    className="categories"
                    style={{
                        marginBottom: 'var(--spacing-8)',
                        display: 'flex',
                        gap: 'var(--spacing-3)',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Link
                        href={`/knowledge-hub${buildQuery({ search, page: 1 })}`}
                        className={`btn ${!category ? 'btn-primary' : 'btn-outline'} btn-sm`}
                    >
                        {t('knowledgeHub.allCategories')}
                    </Link>
                    {categories.map((cat) => (
                        <Link
                            key={cat}
                            href={`/knowledge-hub${buildQuery({ category: cat, search, page: 1 })}`}
                            className={`btn ${category === cat ? 'btn-primary' : 'btn-outline'} btn-sm`}
                        >
                            {t(`categories.${cat}`)}
                        </Link>
                    ))}
                </div>

                <div className="blog-grid">
                    {data.items.length === 0 ? (
                        <p>{t('common.noResults')}</p>
                    ) : (
                        data.items.map((article) => {
                            const title =
                                i18n.language === 'de' && article.title_de ? article.title_de : article.title;
                            const topic =
                                i18n.language === 'de' && article.topic_de ? article.topic_de : article.topic;
                            return (
                                <Link key={article.id} href={`/knowledge-hub/${slugifyRouteValue(title)}`}>
                                <Card hover className="blog-card">
                                    {article.image && (
                                        <img
                                            src={resolveImageUrl(article.image)}
                                            alt={title}
                                            className="blog-image"
                                            loading="lazy"
                                            decoding="async"
                                            width={1200}
                                            height={675}
                                        />
                                    )}
                                    <div className="blog-content">
                                        <h3>{title}</h3>
                                        {topic && <p className="blog-meta">{topic}</p>}
                                    </div>
                                </Card>
                            </Link>
                            );
                        })
                    )}
                </div>

                {data.meta.pages > 1 && (
                    <div className="pagination">
                        <Link
                            href={`/knowledge-hub${buildQuery({
                                page: Math.max(1, page - 1),
                                search,
                                category,
                            })}`}
                            className={`btn btn-outline ${page === 1 ? 'btn-disabled' : ''}`}
                            aria-disabled={page === 1}
                        >
                            {t('common.previous')}
                        </Link>
                        <span>
                            {t('common.page')} {page} {t('common.of')} {data.meta.pages}
                        </span>
                        <Link
                            href={`/knowledge-hub${buildQuery({
                                page: Math.min(data.meta.pages, page + 1),
                                search,
                                category,
                            })}`}
                            className={`btn btn-outline ${page === data.meta.pages ? 'btn-disabled' : ''}`}
                            aria-disabled={page === data.meta.pages}
                        >
                            {t('common.next')}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};
